import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { api, CATEGORIES } from "../lib/api";
import { formatPrice } from "../lib/laCarteData";
import { ImageUpload } from "../components/ImageUpload";
import { AdminFormules } from "./AdminFormules";

const WINE_CATS = ["Vins Rouges", "Vins Blancs", "Vins Rosés"];
const empty = { name: "", description: "", price: "", price_text: "", category: "Entrées", group: "", photo_url: "", available: true, order: 0 };

function MenuForm({ editing, setEditing, groups, onSave, onCancel }) {
  const isWine = WINE_CATS.includes(editing.category);
  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });
  return (
    <form onSubmit={onSave} className="ic-card !transform-none p-6 mb-8 space-y-4" data-testid="menu-edit-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input data-testid="menu-form-name" required className="ic-input" placeholder="Nom du plat *" value={editing.name} onChange={set("name")} />
        <select data-testid="menu-form-category" className="ic-input" value={editing.category} onChange={set("category")}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {isWine ? (
          <input data-testid="menu-form-price-text" className="ic-input sm:col-span-2" placeholder="Prix verre | carafe | bouteille (ex : 5,90 € | 18,50 € | 24,90 €, mettre — si indisponible)" value={editing.price_text || ""} onChange={set("price_text")} />
        ) : (
          <input data-testid="menu-form-price" required type="number" step="0.01" min="0" className="ic-input" placeholder="Prix € *" value={editing.price} onChange={set("price")} />
        )}
        <input data-testid="menu-form-group" list="menu-groups" className="ic-input" placeholder="Sous-rubrique (ex : Pizze Rosse) — facultatif" value={editing.group || ""} onChange={set("group")} />
        <datalist id="menu-groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>
      </div>
      <textarea data-testid="menu-form-description" rows="2" className="ic-input" placeholder="Description" value={editing.description || ""} onChange={set("description")} />
      <div>
        <label className="gold-label block mb-2">Photo du plat (facultatif)</label>
        <ImageUpload testId="menu-form-photo" value={editing.photo_url} onChange={(u) => setEditing({ ...editing, photo_url: u })} />
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-300">
        <input data-testid="menu-form-available" type="checkbox" checked={editing.available} onChange={(e) => setEditing({ ...editing, available: e.target.checked })} className="accent-[#D4AF37]" />
        Disponible (visible sur la carte)
      </label>
      <div className="flex gap-3">
        <button type="submit" data-testid="menu-form-save" className="btn-gold !py-2 text-sm">Enregistrer</button>
        <button type="button" onClick={onCancel} className="btn-outline-gold !py-2 text-sm">Annuler</button>
      </div>
    </form>
  );
}

export default function AdminMenu() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/menu").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const isWine = WINE_CATS.includes(editing.category);
    const payload = {
      ...editing,
      price: isWine ? 0 : Number(editing.price),
      price_text: isWine && editing.price_text ? editing.price_text : null,
      group: editing.group || null,
      photo_url: editing.photo_url || null,
      order: editing.order ?? items.length,
    };
    try {
      if (editing.id) await api.put(`/admin/menu/${editing.id}`, payload);
      else await api.post("/admin/menu", payload);
      toast.success("Plat enregistré — la carte est à jour");
      setEditing(null);
      load();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce plat de la carte ?")) return;
    try { await api.delete(`/admin/menu/${id}`); toast.success("Supprimé"); load(); } catch { toast.error("Erreur"); }
  };

  const groups = [...new Set(items.map((i) => i.group).filter(Boolean))];
  const cats = [...CATEGORIES, ...[...new Set(items.map((i) => i.category))].filter((c) => !CATEGORIES.includes(c))];

  return (
    <div data-testid="admin-menu">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h1 className="font-serif-elegant text-3xl font-bold">La Carte</h1>
        <div className="flex gap-2">
          <Link to="/la-carte" target="_blank" className="btn-outline-gold !py-2 text-sm" data-testid="menu-view-carte"><ExternalLink size={15} /> Voir la carte</Link>
          <button data-testid="menu-add-btn" onClick={() => setEditing({ ...empty })} className="btn-gold !py-2 text-sm"><Plus size={16} /> Ajouter</button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-8">{items.length} références · toute modification est visible immédiatement sur la page publique /la-carte.</p>

      <AdminFormules />

      {editing && <MenuForm editing={editing} setEditing={setEditing} groups={groups} onSave={save} onCancel={() => setEditing(null)} />}

      {cats.filter((c) => items.some((i) => i.category === c)).map((cat) => (
        <div key={cat} className="mb-8" data-testid={`admin-menu-cat-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <h2 className="gold-label mb-3">{cat} <span className="text-gray-600 normal-case tracking-normal">({items.filter((i) => i.category === cat).length})</span></h2>
          <div className="space-y-2">
            {items.filter((i) => i.category === cat).map((item) => (
              <div key={item.id} className="ic-card !transform-none p-4 flex items-center justify-between gap-4" data-testid={`admin-menu-item-${item.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {item.group && <span className="text-[10px] uppercase tracking-wider text-gray-500 mr-2">{item.group}</span>}
                    {item.name} <span className="text-[#D4AF37] ml-2">{formatPrice(item)}</span>
                    {!item.available && <span className="text-xs text-[#ff6b81] ml-2">(indisponible)</span>}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{item.description}</p>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`menu-edit-${item.id}`} onClick={() => setEditing({ ...item, price: item.price || "", price_text: item.price_text || "", group: item.group || "" })} className="p-2 text-gray-400 hover:text-[#D4AF37]"><Pencil size={16} /></button>
                  <button data-testid={`menu-delete-${item.id}`} onClick={() => remove(item.id)} className="p-2 text-gray-400 hover:text-[#ff6b81]"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
